/**
 * Client views read layer (MYAC-930 — client data contract).
 *
 * The doctrine's reusable half: a feature's *reads* are `onSnapshot`
 * subscriptions on `projects/{pid}/views/**`, never HTTP. Two primitives cover
 * the whole read-model shape defined by the spec (§3):
 *
 *  - `useViewDoc`  → a singleton view document (`views/{viewId}`)
 *  - `useViewRows` → a row collection under it (`views/{viewId}/rows`)
 *
 * Deliberately domain-agnostic: any future track (change control, credit
 * status…) subscribes through these, so laziness, teardown and error handling
 * are written once.
 *
 * Two spec guardrails are the caller's job, and both are expressible here:
 *  - **No bare subscriptions** — pass a selective `constraints` list, and use
 *    `enabled` so a screen only subscribes to what it renders (spec §4.5).
 *  - **Selection, never aggregation** — never reduce a *number* out of `rows`;
 *    every count a screen shows is materialized in a view doc (spec §4.0-1).
 */

import {
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'
import { useFirestore } from 'vuefire'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  type Firestore,
  type FirestoreError,
  type QuerySnapshot,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { logger } from '@/utils/logger'

/** The single client-readable read-model subtree of a project. */
export const VIEWS_COLLECTION = 'views'

/** A row view doc plus its Firestore id (write-side plumbing the front only reads). */
export type ViewRow<T> = T & { readonly id: string }

export interface ViewSubscriptionOptions {
  /**
   * Gate the subscription — false unsubscribes and clears. This is how a screen
   * stays lazy (items only for the selected adoption, remediations only on the
   * plan tab) instead of loading every dossier on page open.
   */
  enabled?: MaybeRefOrGetter<boolean>
  /** Injectable Firestore instance (tests / non-component callers). */
  firestore?: Firestore
}

export interface ViewRowsOptions extends ViewSubscriptionOptions {
  /**
   * Make `synced` trustworthy (MYAC-1395) — without it the SDK never reports
   * the cache→server flip when the rows did not change, so a caller waiting on
   * `synced` over a fully cached query waits forever.
   */
  trackServerSync?: boolean
}

interface ViewSubscription {
  pending: Ref<boolean>
  /**
   * A snapshot has arrived FOR THE CURRENT SCOPE.
   *
   * `pending` cannot answer that question: a subscription that was never
   * started (`enabled: false`) is not pending either, so "not pending" reads
   * the same on a family that answered and on one nobody ever asked. R21 is
   * exactly that confusion — the PDF export waited for `!pending`, got `true`
   * on its first iteration for the tab that was not on screen, and printed
   * "this analysis has no clause rows". Reset on every re-scope (project,
   * view, constraints, enablement) and on a failed read, so it never vouches
   * for rows that belong to a previous scope or to no read at all.
   */
  loaded: Ref<boolean>
  /** Non-null when the last snapshot attempt failed — a rules denial lands here. */
  error: Ref<Error | null>
  /** Drop the live subscription; also runs on scope dispose. */
  stop: () => void
}

export interface UseViewDocResult<T> extends ViewSubscription {
  /** null while pending, and when the doc does not exist. */
  data: Ref<T | null>
}

export interface UseViewRowsResult<T> extends ViewSubscription {
  rows: Ref<ViewRow<T>[]>
  /**
   * The rows are the server's answer for the current scope, not the local
   * cache's (MYAC-1395). The two differ right after a one-shot read of a row
   * in the same collection: the SDK keeps that row, and a new listener's first
   * snapshot is served from cache holding it ALONE — one card where the
   * sub-chapter has twenty. Reset on every re-scope, like `loaded`. Only
   * reliable with `trackServerSync`.
   */
  synced: Ref<boolean>
}

export function viewDocPath(projectId: string, viewId: string): string {
  return `projects/${projectId}/${VIEWS_COLLECTION}/${viewId}`
}

/**
 * One row of a view collection, by id.
 *
 * Exported because a row path stopped being purely a subscription detail in
 * 4.8: accepting an action-plan item sends the row's path to the backend as a
 * `task_create` proposal, and the apply layer branches on its shape
 * (`ProposalLocator.REMEDIATION_ROW` matches `/views/assessment_remediations/
 * rows/`). A second spelling of the path in a store would be a second thing to
 * change when the layout moves — and the failure would be silent, since an
 * unrecognised proposal path resolves to `UNKNOWN` and refuses rather than
 * erroring at the call site.
 */
export function viewRowPath(projectId: string, viewId: string, rowId: string): string {
  return `${viewDocPath(projectId, viewId)}/rows/${rowId}`
}

/**
 * Live subscription on one singleton view document.
 *
 * `projectId` / `viewId` may be refs or getters: changing either re-subscribes.
 * A null/empty project id (or `enabled: false`) means "no subscription".
 */
export function useViewDoc<T>(
  projectId: MaybeRefOrGetter<string | null | undefined>,
  viewId: MaybeRefOrGetter<string>,
  options: ViewSubscriptionOptions = {},
): UseViewDocResult<T> {
  const db = options.firestore ?? useFirestore()
  const data = shallowRef<T | null>(null)
  const pending = ref(false)
  const loaded = ref(false)
  const error = ref<Error | null>(null)
  let unsubscribe: Unsubscribe | null = null

  function stop(): void {
    unsubscribe?.()
    unsubscribe = null
    pending.value = false
    loaded.value = false
  }

  watch(
    () => ({
      pid: toValue(projectId) ?? '',
      view: toValue(viewId),
      enabled: toValue(options.enabled) ?? true,
    }),
    ({ pid, view, enabled }) => {
      stop()
      data.value = null
      error.value = null
      if (!pid || !view || !enabled) return
      pending.value = true
      unsubscribe = onSnapshot(
        doc(db, viewDocPath(pid, view)),
        (snap) => {
          data.value = snap.exists() ? (snap.data() as T) : null
          pending.value = false
          loaded.value = true
        },
        (e) => {
          error.value = e
          pending.value = false
          logger.error(`view doc subscription failed: ${view}`, e)
        },
      )
    },
    { immediate: true },
  )

  onScopeDispose(stop)
  return { data, pending, loaded, error, stop }
}

/**
 * Live subscription on a view's `rows` collection.
 *
 * `constraints` may be a ref or getter so a screen can re-scope the query
 * (e.g. `where('adoption_id','==', selected)`) without re-wiring anything.
 * Always pass at least one selective constraint unless the whole collection IS
 * the screen (the product-wide action plan is the one such case, spec §4.6).
 */
export function useViewRows<T>(
  projectId: MaybeRefOrGetter<string | null | undefined>,
  viewId: MaybeRefOrGetter<string>,
  constraints: MaybeRefOrGetter<QueryConstraint[]> = [],
  options: ViewRowsOptions = {},
): UseViewRowsResult<T> {
  const db = options.firestore ?? useFirestore()
  const rows = shallowRef<ViewRow<T>[]>([])
  const pending = ref(false)
  const loaded = ref(false)
  const synced = ref(false)
  const error = ref<Error | null>(null)
  let unsubscribe: Unsubscribe | null = null

  function stop(): void {
    unsubscribe?.()
    unsubscribe = null
    pending.value = false
    loaded.value = false
    synced.value = false
  }

  watch(
    () => ({
      pid: toValue(projectId) ?? '',
      view: toValue(viewId),
      enabled: toValue(options.enabled) ?? true,
      constraints: toValue(constraints),
    }),
    ({ pid, view, enabled, constraints: clauses }) => {
      stop()
      rows.value = []
      error.value = null
      if (!pid || !view || !enabled) return
      pending.value = true
      const q = query(collection(db, `${viewDocPath(pid, view)}/rows`), ...clauses)
      const onNext = (snap: QuerySnapshot) => {
        // Only a metadata flip (cache → server, same rows): keep the array, so
        // nothing downstream re-renders for a change it cannot see.
        if (!(loaded.value && snap.docChanges?.().length === 0)) {
          rows.value = snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }))
        }
        pending.value = false
        loaded.value = true
        synced.value = !snap.metadata?.fromCache
      }
      const onError = (e: FirestoreError) => {
        error.value = e
        pending.value = false
        logger.error(`view rows subscription failed: ${view}`, e)
      }
      unsubscribe = options.trackServerSync
        ? onSnapshot(q, { includeMetadataChanges: true }, onNext, onError)
        : onSnapshot(q, onNext, onError)
    },
    { immediate: true },
  )

  onScopeDispose(stop)
  return { rows, pending, loaded, synced, error, stop }
}

/**
 * One-shot read of a single view row — for on-demand resolutions that must not
 * hold a listener (the summary tab's code-click dialog, spec §4.5). Returns the
 * first match, or null.
 */
export async function readViewRow<T>(
  db: Firestore,
  projectId: string,
  viewId: string,
  constraints: QueryConstraint[],
  options: ViewReadOptions = {},
): Promise<ViewRow<T> | null> {
  try {
    const snap = await getDocs(
      query(collection(db, `${viewDocPath(projectId, viewId)}/rows`), ...constraints, limit(1)),
    )
    const first = snap.docs[0]
    return first ? { ...(first.data() as T), id: first.id } : null
  } catch (e) {
    logger.error(`view row read failed: ${viewId}`, e)
    options.onError?.(e)
    return null
  }
}

/**
 * How a cold read tells its caller it FAILED, as opposed to finding nothing.
 *
 * These helpers swallow errors and resolve to null/empty, which keeps a broken
 * read from taking a screen down with it. The cost is that "no data" and "the
 * read blew up" arrive identically — and the client-data-contract calls that
 * shape out by name: an empty state may render only after a SUCCESSFUL empty
 * read, because a false "all clear" is worse than an error (the Consistency tab
 * shipped a false "no contradiction" exactly this way).
 *
 * ADDITIVE and optional, so every existing caller keeps the old behaviour and
 * only a surface that distinguishes the two has to say so (review #527).
 */
export interface ViewReadOptions {
  onError?: (error: unknown) => void
}

/**
 * One-shot read of a singleton view document — `readViewRow`'s counterpart.
 *
 * For the same class of caller: a resolution that must not hold a listener,
 * from a surface that is not the view's owner. The reference picker reads
 * `assessment_nav` this way to list a project's referentials, where
 * `useViewDoc` would subscribe from inside a dialog that is open for seconds.
 */
export async function readViewDoc<T>(
  db: Firestore,
  projectId: string,
  viewId: string,
  options: ViewReadOptions = {},
): Promise<T | null> {
  try {
    const snap = await getDoc(doc(db, viewDocPath(projectId, viewId)))
    return snap.exists() ? (snap.data() as T) : null
  } catch (e) {
    logger.error(`view doc read failed: ${viewId}`, e)
    options.onError?.(e)
    return null
  }
}

/** Cold multi-row read — same contract as `readViewRow`, for reads that
 * legitimately return several rows (e.g. every consistency constat of ONE task). */
export async function readViewRows<T>(
  db: Firestore,
  projectId: string,
  viewId: string,
  constraints: QueryConstraint[],
  max = 10,
  options: ViewReadOptions = {},
): Promise<ViewRow<T>[]> {
  try {
    const snap = await getDocs(
      query(collection(db, `${viewDocPath(projectId, viewId)}/rows`), ...constraints, limit(max)),
    )
    return snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }))
  } catch (e) {
    logger.error(`view rows read failed: ${viewId}`, e)
    options.onError?.(e)
    return []
  }
}
